import { useMutation } from '@tanstack/react-query';

import { getUser, signIn, signOut } from 'src/api/zing';
import { AuthErrorCode } from 'src/constants';
import { getErrorDetail } from 'src/helpers/error';
import { sendClarityEvent } from 'src/services/analytics/clarity';
import { sendGAEvent } from 'src/services/analytics/ga';
import { getIntl } from 'src/services/intl/intl';
import { showMessage } from 'src/services/notify/notify';
import { queryClient } from 'src/services/react-query/query-client';
import { useUserStore } from 'src/stores/user';

const userState = useUserStore.getState();

export const onUnauthorized: (withNotify?: boolean) => void = (withNotify = true) => {
  userState.setUser(null);
  queryClient.clear();

  if (withNotify) {
    showMessage({
      type: 'error',
      content: getIntl().formatMessage({ id: 'error.session' }),
    });
  }
};

export const useIsUserLoggedIn = () => useUserStore((state) => !!state.user);

export const useIsUserChecked = () => useUserStore((state) => state.isUserChecked);

export const useUser = () => useUserStore((state) => state.user);

export const useUserPlatformId = () => {
  const user = useUser();

  if (!user || user.isAdmin) {
    return '';
  }

  return user.zingPlatformIdsCanAccess[0];
};

export const useUserActions = () => ({
  setUser: userState.setUser,
  getUser: async () => {
    try {
      const user = await getUser();
      userState.setUser(user);
      const userType = user.isAdmin ? 'internal' : 'client';
      sendClarityEvent('user_type', userType);
      sendGAEvent('user_logged_in', { user_type: userType, user_org: user.org, user_id: user.userId }, false);
    } catch (err) {
      const errDetail = getErrorDetail(err);

      if (
        errDetail !== AuthErrorCode.TOKEN_EXPIRED &&
        errDetail !== AuthErrorCode.TOKEN_INCORECT &&
        errDetail !== AuthErrorCode.TOKEN_INVALID &&
        errDetail !== AuthErrorCode.NOT_AUTHENTICATED
      ) {
        showMessage({
          type: 'error',
          content: getIntl().formatMessage({ id: 'error.undefined' }),
        });
      }
    } finally {
      userState.setIsUserChecked(true);
    }
  },
  signOut: async () => {
    await signOut();
    onUnauthorized(false);
  },
});

export const useSignInAction = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: signIn,
  });

  return {
    signIn: mutateAsync,
    isInProgress: isPending,
  };
};
